import React from 'react';
import './App.css';
import logo from './PlainLogo.png';
import founderPhoto from './Todd.jpg';
import dotMascot from './dot-assets/dot-mascot.png';
import dotFood from './dot-assets/food.png';
import dotDrink from './dot-assets/drink.png';
import dotBathroom from './dot-assets/bathroom.png';
import dotHurt from './dot-assets/hurt.png';
import dotFeelings from './dot-assets/feelings.png';
import dotPlaces from './dot-assets/places.png';

const WordCard = ({ emoji, word, tone }) => (
  <div className={`word-card ${tone}`}><span aria-hidden="true">{emoji}</span><strong>{word}</strong></div>
);

function App() {
  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="brand" href="#top" aria-label="JewelTek home"><img src={logo} alt="" /><span>JewelTek</span></a>
        <nav aria-label="Main navigation">
          <a href="#how-it-helps">How it helps</a><a href="#our-story">Our story</a><a className="nav-cta" href="#contact">Stay connected</a>
        </nav>
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow">Built with love. Designed for possibility.</p>
            <h1>Every child deserves a way to be <em>heard.</em></h1>
            <p className="hero-intro">JewelTek creates thoughtful tools that help children with special needs communicate, connect words with meaning, and grow with confidence.</p>
            <div className="hero-actions">
              <a className="button button-primary" href="#our-story">Discover our story <span>→</span></a>
              <a className="button button-secondary" href="#contact">Follow our journey</a>
            </div>
            <p className="founder-note"><span>♥</span> Created by a dad, inspired by his son</p>
          </div>

          <div className="communication-demo" aria-label="Example communication word cards">
            <div className="demo-glow" />
            <div className="demo-panel">
              <div className="demo-topline"><span>My words</span><span className="status-dot">● Ready to talk</span></div>
              <div className="word-grid">
                <WordCard emoji="🥤" word="Drink" tone="blue" /><WordCard emoji="🍎" word="Eat" tone="coral" />
                <WordCard emoji="▶" word="Play" tone="yellow" /><WordCard emoji="✋" word="Stop" tone="rose" />
                <WordCard emoji="🤝" word="Help" tone="purple" /><WordCard emoji="😊" word="Happy" tone="green" />
              </div>
              <div className="sentence-strip"><span>I want</span><strong>Drink</strong><button aria-label="Play sentence">▶</button></div>
            </div>
          </div>
        </section>

        <section className="promise" aria-label="Our promise">
          <p>More than an app</p><h2>A bridge between a child’s world<br />and everyone who loves them.</h2>
          <div className="promise-grid">
            <article><span className="feature-icon blue-icon">●</span><h3>Communicate</h3><p>Give children a clear, approachable way to express needs, feelings, and ideas.</p></article>
            <article><span className="feature-icon yellow-icon">◆</span><h3>Learn</h3><p>Build associations between words, images, and actions through everyday use.</p></article>
            <article><span className="feature-icon coral-icon">♥</span><h3>Connect</h3><p>Create more opportunities for understanding between children, families, and caregivers.</p></article>
          </div>
        </section>

        <section className="story" id="our-story">
          <div className="story-photo"><img src={founderPhoto} alt="Todd, founder of JewelTek, seated in a peaceful garden" /><div className="photo-caption"><span>“</span><p>I started with one goal:<br /><strong>help my son be understood.</strong></p></div></div>
          <div className="story-copy">
            <p className="eyebrow">Why JewelTek exists</p><h2>Built for my son.<br />Growing for every child.</h2>
            <p>I’m a single father to a wonderful son with nonverbal autism. Like every parent, I wanted him to have every opportunity—to share what he needs, connect with the people around him, and keep learning in a way that works for him.</p>
            <p>So I built a communication app for him. It was designed to help him connect each word with its meaning and learn how to use it in everyday life.</p>
            <p>What began as one father trying to help his child became something bigger: the realization that this work could help many more children and families. That is the future of JewelTek.</p>
            <div className="signature"><span>Todd</span><small>Founder & Dad</small></div>
          </div>
        </section>

        <section className="how" id="how-it-helps">
          <div className="section-heading"><p className="eyebrow">Designed around real life</p><h2>Simple tools. Meaningful progress.</h2><p>We’re creating experiences that meet children where they are and help families celebrate every step forward.</p></div>
          <div className="steps">
            <article><b>01</b><h3>See it</h3><p>Friendly visuals make words easier to recognize and understand.</p></article>
            <article><b>02</b><h3>Hear it</h3><p>Spoken words reinforce meaning and support communication.</p></article>
            <article><b>03</b><h3>Use it</h3><p>Everyday repetition helps turn recognition into confident expression.</p></article>
          </div>
        </section>

        <section className="dot-future" id="dot">
          <div className="dot-showcase" aria-label="Preview of the upcoming DOT communication app">
            <div className="dot-halo" />
            <div className="dot-phone">
              <div className="dot-app-header"><div><img src={dotMascot} alt="" /><b>DOT</b></div><span>🔊 Speak</span><span>💬 Parent</span></div>
              <div className="dot-app-grid">
                <div className="peach"><img src={dotFood} alt="" /><b>Food</b></div>
                <div className="sky"><img src={dotDrink} alt="" /><b>Drink</b></div>
                <div className="aqua"><img src={dotBathroom} alt="" /><b>Bathroom</b></div>
                <div className="pink"><img src={dotHurt} alt="" /><b>Hurt</b></div>
                <div className="yellow"><img src={dotFeelings} alt="" /><b>Feelings</b></div>
                <div className="green"><img src={dotPlaces} alt="" /><b>Places</b></div>
              </div>
              <div className="dot-app-sentence"><small>DOT SAYS</small><p>Tap a picture to build your message</p><div><b>▶ Speak</b><span>⌫ Undo</span><span>× Clear</span></div></div>
            </div>
            <img className="dot-real-mascot" src={dotMascot} alt="DOT, JewelTek’s ladybug communication companion" />
          </div>
          <div className="dot-copy">
            <span className="release-pill">Future JewelTek release</span>
            <p className="eyebrow">Meet the real DOT</p>
            <h2>Communication made<br />more approachable.</h2>
            <p>DOT is an upcoming picture-based communication companion designed to help autistic children express everyday needs, build messages, and hear their words spoken aloud.</p>
            <div className="dot-feature-list"><span>Picture-led choices</span><span>Speak or silent modes</span><span>Parent messaging</span><span>Food, feelings & places</span></div>
            <p className="dot-note"><span>✦</span> Built to feel calm, friendly, and easy to understand—for children and the people who support them.</p>
          </div>
        </section>

        <section className="contact" id="contact">
          <div><p className="eyebrow">The next chapter is beginning</p><h2>Help us build a more<br />connected world.</h2></div>
          <div className="contact-copy"><p>JewelTek is evolving, and we’d love to keep families, educators, therapists, and supporters close as we grow.</p><a className="button button-light" href="mailto:JewellTek@gmail.com">Get in touch <span>→</span></a></div>
        </section>
      </main>

      <footer><a className="brand" href="#top"><img src={logo} alt="" /><span>JewelTek</span></a><p>Technology with purpose. Built from the heart.</p><p>© {new Date().getFullYear()} JewelTek</p></footer>
    </div>
  );
}

export default App;
